import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PageSEO } from "../components/SEO";

export default function Register({ setUser }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referrer, setReferrer] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Determine API base at runtime based on current domain
  const API_BASE = useMemo(() => {
    const hostname = window.location.hostname;
    if (hostname === 'sqawk.app' || hostname === 'www.sqawk.app') {
      return '';
    }
    return process.env.REACT_APP_BACKEND_URL || '';
  }, []);

  // Check for referral code in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      validateReferralCode(ref.toUpperCase());
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    try {
      const response = await fetch(`${API_BASE}/api/referral/validate/${code}`);
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setReferrer(data.referrer);
        }
      }
    } catch (error) {
      console.error("Error validating referral:", error);
    }
  };

  const isAdult = () => {
    if (!birthDate) return false;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age >= 18;
  };

  const goStep2 = () => {
    if (!name.trim()) { setError('Enter your name'); return; }
    if (!username.trim() || username.length < 3) { setError('Username must be 3+ chars'); return; }
    if (!email.includes('@')) { setError('Enter valid email'); return; }
    if (password.length < 6) { setError('Password must be 6+ chars'); return; }
    setError('');
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!birthDate) { setError('Enter birth date'); return; }
    if (!isAdult()) { setError('Must be 18+'); return; }
    if (!ageConfirmed) { setError('Confirm you are 18+'); return; }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          username: username.trim().toLowerCase(),
          email: email.trim().toLowerCase(),
          password,
          birth_date: birthDate,
          referral_code: referralCode || undefined
        }),
      });

      if (!response.ok) {
        let errorMsg = 'Registration failed';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {}
        setError(errorMsg);
        setLoading(false);
        return;
      }

      const data = await response.json();
      
      // Check if account is pending approval
      if (data.status === 'pending_approval') {
        navigate('/pending-approval', { replace: true });
        return;
      }
      
      if (data.session_token) {
        localStorage.setItem('sqawk_session', data.session_token);
        localStorage.setItem('sqawk_session_start', Date.now().toString());
        if (data.user) setUser(data.user);
        navigate('/dashboard', { replace: true });
        return;
      } else {
        setError('Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Unable to connect. Please try again.');
    }
    
    setLoading(false);
  };

  const inputStyle = {
    width: '100%',
    padding: '14px',
    marginBottom: '12px',
    background: '#222',
    border: '1px solid #333',
    borderRadius: '8px',
    color: 'white',
    fontSize: '16px',
    boxSizing: 'border-box'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <PageSEO.Register />
      <form onSubmit={step === 1 ? (e) => { e.preventDefault(); goStep2(); } : handleSubmit} style={{
        width: '100%',
        maxWidth: '320px',
        background: '#111',
        padding: '30px',
        borderRadius: '12px'
      }}>
        {/* sQawk Logo */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_sqawk-social-4/artifacts/fvlilxsv_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2007_59_06%20PM.png" 
            alt="sQawk" 
            style={{ width: '80px', height: '80px', objectFit: 'contain' }}
          />
        </div>
        
        <h1 style={{
          color: '#10b981',
          fontSize: '24px',
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          {step === 1 ? 'Create Account' : 'Verify Age'}
        </h1>

        {error && (
          <div style={{
            background: '#ff000020',
            color: '#ff6b6b',
            padding: '10px',
            borderRadius: '6px',
            marginBottom: '15px',
            textAlign: 'center',
            fontSize: '14px'
          }}>{error}</div>
        )}

        {referrer && (
          <div style={{
            background: 'linear-gradient(135deg, #10b98120 0%, #06b6d420 100%)',
            border: '1px solid #10b98140',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <img 
              src={referrer.profile_picture || 'https://customer-assets.emergentagent.com/job_sqawk-social-4/artifacts/su4m18ey_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2008_01_31%20PM.png'} 
              alt={referrer.name}
              style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }}
            />
            <div style={{ flex: 1 }}>
              <p style={{ color: '#10b981', fontSize: '12px', margin: 0 }}>Invited by</p>
              <p style={{ color: 'white', fontSize: '14px', fontWeight: '500', margin: 0 }}>{referrer.name}</p>
            </div>
            <span style={{ fontSize: '20px' }}>🎉</span>
          </div>
        )}

        {step === 1 && (
          <>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Full Name"
              style={inputStyle}
            />
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value.replace(/\s/g, ''))}
              placeholder="Username"
              style={inputStyle}
            />
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email"
              style={inputStyle}
            />
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password (6+ chars)"
              style={inputStyle}
            />
            
            {/* Referral Code Input */}
            {!referrer && (
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="text"
                  value={referralCode}
                  onChange={e => {
                    const code = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    setReferralCode(code);
                    if (code.length >= 6) {
                      validateReferralCode(code);
                    } else {
                      setReferrer(null);
                    }
                  }}
                  placeholder="Referral Code (optional)"
                  maxLength={12}
                  style={{
                    ...inputStyle,
                    marginBottom: '5px',
                    textTransform: 'uppercase',
                    letterSpacing: '2px'
                  }}
                />
                <p style={{ color: '#666', fontSize: '11px', margin: 0 }}>
                  Have a friend on sQawk? Enter their referral code!
                </p>
              </div>
            )}
            
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '14px',
                background: '#10b981',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Continue
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <label style={{ color: '#aaa', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              Date of Birth
            </label>
            <input
              type="date"
              value={birthDate}
              onChange={e => setBirthDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              style={inputStyle}
            />
            
            {birthDate && isAdult() && (
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                color: '#ccc',
                fontSize: '14px',
                marginBottom: '20px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={e => setAgeConfirmed(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                I confirm I am 18 or older
              </label>
            )}

            {birthDate && !isAdult() && (
              <p style={{ color: '#ff6b6b', fontSize: '14px', marginBottom: '15px' }}>
                You must be 18 or older to join
              </p>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || !isAdult() || !ageConfirmed}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: loading || !isAdult() || !ageConfirmed ? '#444' : '#10b981',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: loading ? 'wait' : 'pointer'
                }}
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <a href="/login" style={{ color: '#10b981', fontSize: '14px' }}>
            Already have account? Login
          </a>
        </div>
      </form>
    </div>
  );
}
